import React, { ChangeEvent } from "react";
import { RouteComponentProps } from "react-router-dom";

import { V2 } from "../../Api";
import { formatDateLocal, formatUSD } from "../../Utilities";
import withTitle from "../withTitle";
import HasRight from "../HasRight";
import ViewToolbar from "../ViewToolbar";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";

interface IStatementAccountViewState {
    isLoading: boolean | string;
    secondaryError?: boolean | string;

    account: any;
}

interface IStatementAccountViewProps extends RouteComponentProps<any> {

}

class StatementAccountView extends React.Component<IStatementAccountViewProps, IStatementAccountViewState> {
    constructor(props: Readonly<IStatementAccountViewProps>) {
        super(props);

        const state: IStatementAccountViewState = {
            isLoading: true,

            account: {
                contact: {},
                statements: []
            }
        };

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.markAsPosted = this.markAsPosted.bind(this);
        this.doOptOut = this.doOptOut.bind(this);
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        const account = this.state.account;
        const lastStatement = this.state.account.statements[0] || {};

        if (this.state.isLoading === true) {
            return <div className="viewer-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }
        else if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-text">{this.state.isLoading}</div>
            </div>;
        }

        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="stmt:acct:write" condition={!!account.contact.canEmailStatements}>
                            <button type="button" className="text-with-icon" onClick={this.doOptOut}><i className="icon-envelope" /><span>Opt Out of Emailed Statements</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Account Information</h3>
                        </header>

                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>Name</dt>
                                    <dd>{account.name}</dd>
                                </div>
                                <div>
                                    <dt>Account Number</dt>
                                    <dd>{account.externalAccountId}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Can email?</dt>
                                    <dd>
                                        {!!account.contact.canEmailStatements
                                            ? "Yes"
                                            : `No \u2014 ${this.optOutReasonToText(account.contact.emailOptOutReason)}`}
                                    </dd>
                                    {
                                        !account.contact.canEmailStatements && account.contact.dateEmailOptedOut
                                            ? <dd><small>Since {formatDateLocal(account.contact.dateEmailOptedOut)}</small></dd>
                                            : null
                                    }
                                </div>
                                <div>
                                    <dt>Last email address</dt>
                                    <dd><code>{account.contact.emailAddress || "-"}</code></dd>
                                </div>
                            </div>
                        </dl>
                    </section>
                    <section className="info-block">
                        <header>
                            <h3>Last statement</h3>
                        </header>

                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>Date Generated</dt>
                                    <dd>{formatDateLocal(lastStatement.dateGenerated)}</dd>
                                </div>
                                <div>
                                    <dt>Date Due</dt>
                                    <dd>{formatDateLocal(lastStatement.dateDue)}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Status</dt>
                                    <dd>{this.statementStatusToText(lastStatement.status, !!account.contact.canEmailStatements)}</dd>
                                </div>
                                <div>
                                    <dt>Last Update</dt>
                                    <dd>{formatDateLocal(lastStatement.lastUpdate)}</dd>
                                </div>
                            </div>
                        </dl>
                    </section>
                </div>
                &nbsp;
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-smallest">Statement #</th>
                            <th>Name</th>
                            <th>Date Generated</th>
                            <th>Date Due</th>
                            <th>$ Due</th>
                            <th>$ Paid</th>
                            <th className="col-smallest">Status</th>
                            <th className="col-smallest" title="Has PDF?"><i className="icon-file-pdf-o" /></th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Statements</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            account.statements.length === 0
                                ? <tr><td colSpan={10}>No statements found</td></tr>
                                : account.statements.map((a: any) => (
                                    <tr key={a.id} tabIndex={0}>
                                        <td className="inline col-center" data-caption="Account: ">{a.externalStatementId}</td>
                                        <td className="inline" data-caption="Name: ">{a.name}</td>
                                        <td className="inline" data-caption="Date Generated: ">{formatDateLocal(a.dateGenerated) || "-"}</td>
                                        <td className="inline" data-caption="Date Due: ">{formatDateLocal(a.dateDue) || "-"}</td>
                                        <td className="inline col-right-vt col-smallest" data-caption="Amount due: ">{formatUSD(a.amount)}</td>
                                        <td className="inline col-right-vt col-smallest" data-caption="Amount paid: ">{formatUSD(a.amountPaid)}</td>
                                        <td className="inline" data-caption="Status: ">{this.statementStatusToText(a.status, !!account.contact.canEmailStatements) || "-"}</td>
                                        <td className="inline col-center" data-caption="Has PDF?: ">{a.hasPdf ? <i className="icon-file-pdf-o" style={{ color: "#00f", cursor: "pointer" }} title="View PDF" onClick={() => this.openPdf(a.id)} /> : null}</td>
                                        <td className="inline col-nopad" data-caption="Mark posted: ">
                                            <HasRight right="stmt:read">{
                                                a.status === 11
                                                    ? <button type="button" className="button btn-light small text-with-icon" disabled={this.state.secondaryError === true} onClick={() => this.markAsPosted(a.id)}><i className="icon-check" /><span>Mark Posted</span></button>
                                                    : null
                                            }</HasRight>
                                        </td>
                                    </tr>
                                ))}
                    </tbody>
                </table>
                {this.state.secondaryError === true
                    ? <div className="spinner lds-dual-ring" />
                    : typeof this.state.secondaryError === "string"
                        ? <div className="error-text">&nbsp;<br />{this.state.secondaryError}</div>
                        : null
                }
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        } as any, () => this.loadData());
    }

    private async loadData(skipSpinner = false) {
        try {
            if (!skipSpinner) {
                this.setState({
                    isLoading: true
                });
            }

            const account = await V2.get<any>(`statements/account/by-internal-id/${this.props.match.params.id}`);

            this.setState({
                isLoading: false,
                secondaryError: false,

                account,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load account information"
            });
        }
    }

    private statementStatusToText(status: number, canEmail = true) {
        switch (status) {
            case 0: return canEmail ? "Not sent" : "Not printed";
            case 1: return "Sent";
            case 2: return "Print Queued";
            case 3: return "Printed";
            case 4: return "Skipped";
            case 10: return "Payment Authorized";
            case 11: return "Payment Settled";
            case 12: return "Payment Rejected";
            case 13: return "Payment Disputed";
            case 20: return "Payment Posted";
            case 21: return "Voided";
            default: return status;
        }
    }

    private optOutReasonToText(reason: string) {
        switch (reason) {
            case "invalid_email": return "Invalid email address";
            case "user_request": return "Requested unsubscribe";
            case "bounce": return "Email bounced";
            case "spam_complaint": return "Spam complaint";
            case "email_removed": return "Email removed";
            case null: return "No email address";
            default: return reason;
        }
    }

    private async markAsPosted(id: string) {
        try {
            this.setState({
                secondaryError: true
            });

            const data = {
                status: "paymentposted"
            };

            await V2.post(`statements/${id}/status`, data);

            await this.loadData(true);
        }
        catch (e) {
            this.setState({
                secondaryError: e.message || "Could not update statement status"
            });
        }
    }

    private openPdf(id: string) {
        V2.get<Blob>(`statements/${id}/pdf`)
            .then(v => {
                const blob = URL.createObjectURL(v);
                const wnd = window.open(blob, `stmt_${id}`, "resizable,scrollbars,status");

                if (wnd) {
                    wnd.addEventListener("unload", e => {
                        if ((e.currentTarget! as Window).location.protocol === "blob:") {
                            URL.revokeObjectURL(blob);
                        }
                    });
                }
            })
            .catch(() => { });
    }

    private async doOptOut() {
        try {
            const data = {
                emailAddress: this.state.account.contact.emailAddress,
                reasonType: "user_request"
            };

            await V2.post(`statements/account/unsubscribe/by-email`, data);

            this.loadData(true);
        }
        catch (e) {
            // TODO: Figure out how to display this error?
        }
    }
}

export default withTitle("Statement Account", StatementAccountView);
